import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlarmClock, PhoneCall, MessageSquare, X, RefreshCw, User, Wrench, IndianRupee, Hash, FileSpreadsheet, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatBill } from "@/lib/format";
import { toast } from "sonner";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const statusColor = (s) => {
  if (s === "Delivered") return "bg-purple-50 text-purple-700 border-purple-200";
  if (s === "Completed") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "In Progress") return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

export default function UrgentDialog({ open, onOpenChange, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [waRow, setWaRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/urgent");
      setItems(data?.items || []);
      if (onCountChange) onCountChange(data?.count || 0);
    } finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const clearUrgent = async (row) => {
    try {
      await api.patch(`/receipts/${row.id}/urgent`, { is_urgent: false });
      toast.success(`Removed urgent flag on ${formatBill(row.doc_no)}`);
      await load();
    } catch { toast.error("Could not update"); }
  };

  const openWhatsApp = (row) => {
    if (!row.mobile) return toast.error("No mobile number on record");
    setWaRow(row);
  };

  const downloadFile = async (format) => {
    if (items.length === 0) return toast.info("Nothing to export — no urgent applications right now");
    const path = format === "pdf" ? "/urgent-work/export-pdf" : "/urgent-work/export";
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `urgent-work-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Urgent work ${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] flex flex-col" data-testid="urgent-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
              <AlarmClock className="h-5 w-5 text-red-600" />
            </span>
            <div>
              <DialogTitle className="font-display text-xl tracking-tight text-red-700">
                Urgent Work
              </DialogTitle>
              <DialogDescription className="text-xs">
                Applications flagged as urgent by customers — priority follow-up required.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-between mt-1 mb-2 shrink-0">
          <div className="text-sm text-slate-600">
            Showing <span className="font-bold text-red-700">{items.length}</span> urgent {items.length === 1 ? "application" : "applications"}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold" onClick={() => downloadFile("xlsx")} data-testid="urgent-excel-btn">
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
            </Button>
            <Button size="sm" className="h-8 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold" onClick={() => downloadFile("pdf")} data-testid="urgent-pdf-btn">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="urgent-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1" data-testid="urgent-cards-scroll">
          {items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">
              <AlarmClock className="h-10 w-10 mx-auto mb-2 text-slate-200" />
              No urgent applications right now.
            </div>
          ) : (
            items.map((r, idx) => (
              <div
                key={r.id}
                className="border border-red-200 bg-gradient-to-br from-red-50/70 to-white rounded-lg shadow-sm overflow-hidden"
                data-testid={`urgent-card-${r.id}`}
              >
                {/* Header strip */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-red-600 text-white">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/20 text-[10px] font-black">
                      {idx + 1}
                    </span>
                    <Hash className="h-3.5 w-3.5 opacity-80 shrink-0" />
                    <span className="font-mono-num font-bold tracking-wide truncate">{formatBill(r.doc_no)}</span>
                    <span className="mx-1 opacity-40">·</span>
                    <User className="h-3.5 w-3.5 opacity-80 shrink-0" />
                    <span className="font-semibold truncate">{r.customer_name || "—"}</span>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${statusColor(r.status)} shrink-0`}>
                    {r.status || "Pending"}
                  </span>
                </div>

                {/* Reason — the star of the show */}
                <div className="px-4 py-3 border-b border-red-100 bg-red-50/40">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-red-700 mb-1">Reason for Urgency</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap break-words leading-relaxed" data-testid={`urgent-reason-full-${r.id}`}>
                    {r.urgent_reason || <span className="italic text-slate-400">No reason recorded</span>}
                  </div>
                </div>

                {/* Details grid */}
                <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                  <Detail icon={Wrench} label="Service" value={r.service_name || "—"} />
                  <Detail icon={PhoneCall} label="Mobile" value={r.mobile || "—"} mono />
                  <Detail icon={User} label="Operator" value={r.operator || "Unassigned"} />
                  <Detail icon={IndianRupee} label="Balance" value={inr(r.balance)} valueClass={r.balance > 0 ? "text-red-600 font-bold" : "text-emerald-700 font-semibold"} />
                </div>

                {/* Actions */}
                <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
                  {r.mobile && (
                    <>
                      <a
                        href={`tel:${r.mobile}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                        data-testid={`urgent-call-${r.id}`}
                      >
                        <PhoneCall className="h-3.5 w-3.5" /> Call
                      </a>
                      <button
                        onClick={() => openWhatsApp(r)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-semibold"
                        data-testid={`urgent-wa-${r.id}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5" /> WhatsApp
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => clearUrgent(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold"
                    title="Clear urgent flag"
                    data-testid={`urgent-clear-${r.id}`}
                  >
                    <X className="h-3.5 w-3.5" /> Clear Urgent
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)} · ${waRow.service_name || ""}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Message"
        />
      )}
    </Dialog>
  );
}

function Detail({ icon: Icon, label, value, mono, valueClass }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-sm text-slate-800 truncate ${mono ? "font-mono-num" : ""} ${valueClass || ""}`} title={value}>
        {value}
      </div>
    </div>
  );
}
